import { STORE_KEYS } from "@/other/constants/StoreKeys";
import type { MarketingAsset } from "@/bryntum-lib/models/MarketingAsset";

export type MarketingAssetPickerEmptyState = {
  emptyTitle: string;
  emptyDescription: string;
  description: string;
};

export type SelectableMarketingAsset = {
  id: number;
  fileId: number;
  alt: string;
};

function assetsForProblemHub(
  allAssets: MarketingAsset[],
  problemHubId: number | null,
): MarketingAsset[] {
  if (problemHubId == null) return allAssets;
  return allAssets.filter((asset) => asset.tags?.includes(problemHubId));
}

export function resolveMarketingAssetPickerEmptyState(
  project: any,
  problemHubId: number | null,
  hubLabel: string | null,
): MarketingAssetPickerEmptyState {
  const description =
    problemHubId != null
      ? `Showing assets tagged for ${hubLabel ?? "this work file's problem hub"}`
      : "Browse and add marketing assets as linked evidence";

  const defaults: MarketingAssetPickerEmptyState = {
    emptyTitle: "No marketing assets on this board",
    emptyDescription:
      "Upload and organize marketing assets first (for example from Coverage), then open this picker again to link them as evidence.",
    description,
  };

  if (!project) return defaults;

  const assetStore = project.getCrudStore(STORE_KEYS.marketingAssetStore);
  const allAssets = (assetStore?.query(() => true) ?? []) as unknown as MarketingAsset[];
  if (allAssets.length === 0) return defaults;

  const scoped = assetsForProblemHub(allAssets, problemHubId);
  if (problemHubId != null && scoped.length === 0) {
    return {
      description,
      emptyTitle: hubLabel
        ? `No assets tagged for "${hubLabel}"`
        : "No assets tagged for this work file's problem hub",
      emptyDescription:
        "This picker only lists marketing assets tagged with the work file's problem hub. Tag assets with that hub in Marketing Assets / Coverage, then try again.",
    };
  }

  return defaults;
}

export function listSelectableMarketingAssets(
  project: any,
  problemHubId: number | null,
): SelectableMarketingAsset[] {
  const assetStore = project?.getCrudStore(STORE_KEYS.marketingAssetStore);
  const allAssets = (assetStore?.query(() => true) ?? []) as unknown as MarketingAsset[];
  return assetsForProblemHub(allAssets, problemHubId).map((asset) => ({
    id: asset.id as number,
    fileId: asset.file,
    alt: asset.altText || asset.caption || "",
  }));
}
